import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Trophy, Heart, Clock, Zap,
  Play, Pause, Volume2, VolumeX, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const LANE_COUNT = 3;
const LANE_WIDTH = 60;

const RushRunnerGame = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  
  // Responsive canvas size
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 450 });
  
  // Game state
  const [score, setScore] = useState(0);
  const [coins, setCoins] = useState(0);
  const [distance, setDistance] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [highScore, setHighScore] = useState(0);
  const [multiplier, setMultiplier] = useState(1);
  const [powerUp, setPowerUp] = useState(null);
  
  // Session state
  const [session, setSession] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [endingGame, setEndingGame] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  // Game objects ref
  const gameState = useRef({
    player: { 
      lane: 1, 
      y: 350, 
      width: 30, 
      height: 45,
      isJumping: false,
      isSliding: false,
      jumpVelocity: 0,
      baseY: 350
    },
    obstacles: [],
    collectibles: [],
    speed: 5,
    baseSpeed: 5,
    maxSpeed: 15,
    frameCount: 0,
    trackOffset: 0,
    invincible: false,
    invincibleTimer: 0
  });

  // Update canvas size based on screen
  useEffect(() => {
    const updateSize = () => {
      const maxWidth = Math.min(window.innerWidth - 32, 350);
      const height = Math.min(maxWidth * 1.4, 500);
      setCanvasSize({ width: maxWidth, height });
      
      // Update player base position
      gameState.current.player.baseY = height - 100;
      gameState.current.player.y = height - 100;
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Load session on mount
  useEffect(() => {
    const stored = localStorage.getItem('currentGameSession');
    if (stored) {
      const sessionData = JSON.parse(stored);
      if (sessionData.gameSlug === 'rush-runner') {
        setSession(sessionData);
        setLives(sessionData.lives === -1 ? 999 : sessionData.lives);
        // Load high score from localStorage
        const savedHigh = localStorage.getItem('rushRunnerHighScore');
        if (savedHigh) setHighScore(parseInt(savedHigh));
      } else {
        navigate('/games');
      }
    } else {
      navigate('/games');
    }
  }, [navigate]);

  // Timer
  useEffect(() => {
    let timer;
    if (gameStarted && !gameOver && !isPaused) {
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameStarted, gameOver, isPaused]);

  // Get lane X position
  const getLaneX = useCallback((lane) => {
    const { width } = canvasSize;
    const startX = (width - (LANE_COUNT * LANE_WIDTH)) / 2;
    return startX + (lane * LANE_WIDTH) + (LANE_WIDTH / 2);
  }, [canvasSize]);

  // Initialize game
  const initGame = useCallback(() => {
    const state = gameState.current;
    const { height } = canvasSize;
    state.player = {
      lane: 1,
      y: height - 100,
      width: 30,
      height: 45,
      isJumping: false,
      isSliding: false,
      jumpVelocity: 0,
      baseY: height - 100
    };
    state.obstacles = [];
    state.collectibles = [];
    state.speed = 5;
    state.frameCount = 0;
    state.trackOffset = 0;
    state.invincible = false;
    state.invincibleTimer = 0;
    
    setScore(0);
    setCoins(0);
    setDistance(0);
    setMultiplier(1);
    setPowerUp(null);
    setGameOver(false);
    setGameStarted(true);
  }, [canvasSize]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver || isPaused || !gameStarted) return;
      
      const state = gameState.current;
      
      switch(e.code) {
        case 'ArrowLeft':
        case 'KeyA':
          if (state.player.lane > 0) {
            state.player.lane--;
          }
          break;
        case 'ArrowRight':
        case 'KeyD':
          if (state.player.lane < LANE_COUNT - 1) {
            state.player.lane++;
          }
          break;
        case 'ArrowUp':
        case 'KeyW':
        case 'Space':
          if (!state.player.isJumping && !state.player.isSliding) {
            state.player.isJumping = true;
            state.player.jumpVelocity = -15;
          }
          break;
        case 'ArrowDown':
        case 'KeyS':
          if (!state.player.isJumping) {
            state.player.isSliding = true;
            state.player.height = 30;
            setTimeout(() => {
              state.player.isSliding = false;
              state.player.height = 60;
            }, 500);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameOver, isPaused, gameStarted]);

  // Touch controls for mobile
  const handleSwipe = useCallback((direction) => {
    if (gameOver || isPaused || !gameStarted) return;
    
    const state = gameState.current;
    
    switch(direction) {
      case 'left':
        if (state.player.lane > 0) state.player.lane--;
        break;
      case 'right':
        if (state.player.lane < LANE_COUNT - 1) state.player.lane++;
        break;
      case 'up':
        if (!state.player.isJumping && !state.player.isSliding) {
          state.player.isJumping = true;
          state.player.jumpVelocity = -15;
        }
        break;
      case 'down':
        if (!state.player.isJumping) {
          state.player.isSliding = true;
          state.player.height = 30;
          setTimeout(() => {
            state.player.isSliding = false;
            state.player.height = 60;
          }, 500);
        }
        break;
    }
  }, [gameOver, isPaused, gameStarted]);

  // Spawn obstacles
  const spawnObstacle = useCallback(() => {
    const state = gameState.current;
    const lane = Math.floor(Math.random() * LANE_COUNT);
    const types = ['barrier', 'low_barrier', 'train'];
    const type = types[Math.floor(Math.random() * types.length)];
    
    let height = 45;
    let width = 40;
    
    if (type === 'low_barrier') {
      height = 22;
    } else if (type === 'train') {
      height = 60;
      width = 45;
    }
    
    state.obstacles.push({
      lane,
      y: -height,
      width,
      height,
      type
    });
  }, []);

  // Spawn collectibles
  const spawnCollectible = useCallback(() => {
    const state = gameState.current;
    const lane = Math.floor(Math.random() * LANE_COUNT);
    const types = ['coin', 'coin', 'coin', 'magnet', 'multiplier', 'shield'];
    const type = types[Math.floor(Math.random() * types.length)];
    
    state.collectibles.push({
      lane,
      y: -25,
      width: 20,
      height: 20,
      type
    });
  }, []);

  // Game loop
  useEffect(() => {
    if (!gameStarted || gameOver || isPaused) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = gameState.current;
    const { width, height } = canvasSize;

    const gameLoop = () => {
      state.frameCount++;
      
      // Update speed gradually
      if (state.frameCount % 500 === 0 && state.speed < state.maxSpeed) {
        state.speed += 0.5;
      }

      // Update score and distance
      if (state.frameCount % 10 === 0) {
        setScore(prev => prev + Math.floor(state.speed * multiplier));
        setDistance(prev => prev + 1);
      }

      // Spawn obstacles
      if (state.frameCount % Math.max(40, 80 - Math.floor(state.speed * 2)) === 0) {
        spawnObstacle();
      }

      // Spawn collectibles
      if (state.frameCount % 60 === 0) {
        spawnCollectible();
      }

      // Update invincibility
      if (state.invincible) {
        state.invincibleTimer--;
        if (state.invincibleTimer <= 0) {
          state.invincible = false;
        }
      }

      // Update player jump
      if (state.player.isJumping) {
        state.player.y += state.player.jumpVelocity;
        state.player.jumpVelocity += 0.8; // Gravity
        
        if (state.player.y >= state.player.baseY) {
          state.player.y = state.player.baseY;
          state.player.isJumping = false;
          state.player.jumpVelocity = 0;
        }
      }

      // Update track offset for visual scrolling
      state.trackOffset = (state.trackOffset + state.speed) % 40;

      // Clear canvas
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, width, height);

      // Draw track/road
      const trackStartX = (width - (LANE_COUNT * LANE_WIDTH)) / 2;
      
      // Draw track background
      ctx.fillStyle = '#2d2d44';
      ctx.fillRect(trackStartX - 8, 0, LANE_COUNT * LANE_WIDTH + 16, height);
      
      // Draw lane dividers
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 2;
      ctx.setLineDash([25, 15]);
      
      for (let i = 1; i < LANE_COUNT; i++) {
        const x = trackStartX + (i * LANE_WIDTH);
        ctx.beginPath();
        ctx.moveTo(x, -state.trackOffset);
        for (let y = -state.trackOffset; y < height + 40; y += 40) {
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // Draw track edges
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(trackStartX - 4, 0);
      ctx.lineTo(trackStartX - 4, height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(trackStartX + LANE_COUNT * LANE_WIDTH + 4, 0);
      ctx.lineTo(trackStartX + LANE_COUNT * LANE_WIDTH + 4, height);
      ctx.stroke();

      // Update and draw obstacles
      const playerX = getLaneX(state.player.lane);
      const playerLeft = playerX - state.player.width / 2;
      const playerRight = playerX + state.player.width / 2;
      const playerTop = state.player.y;
      const playerBottom = state.player.y + state.player.height;

      state.obstacles = state.obstacles.filter(obs => {
        obs.y += state.speed;
        
        // Draw obstacle
        const obsX = getLaneX(obs.lane);
        
        if (obs.type === 'barrier') {
          ctx.fillStyle = '#ef4444';
          ctx.fillRect(obsX - obs.width/2, obs.y, obs.width, obs.height);
          ctx.fillStyle = '#fca5a5';
          ctx.fillRect(obsX - obs.width/2 + 4, obs.y + 4, obs.width - 8, 8);
        } else if (obs.type === 'low_barrier') {
          ctx.fillStyle = '#f97316';
          ctx.fillRect(obsX - obs.width/2, obs.y, obs.width, obs.height);
        } else if (obs.type === 'train') {
          ctx.fillStyle = '#6366f1';
          ctx.fillRect(obsX - obs.width/2, obs.y, obs.width, obs.height);
          ctx.fillStyle = '#818cf8';
          ctx.fillRect(obsX - obs.width/2, obs.y + 8, obs.width, 12);
          ctx.fillRect(obsX - obs.width/2, obs.y + 42, obs.width, 12);
        }

        // Collision detection
        if (!state.invincible) {
          const obsLeft = obsX - obs.width / 2;
          const obsRight = obsX + obs.width / 2;
          const obsTop = obs.y;
          const obsBottom = obs.y + obs.height;

          if (playerRight > obsLeft && playerLeft < obsRight &&
              playerBottom > obsTop && playerTop < obsBottom) {
            
            if (obs.type === 'low_barrier' && state.player.isJumping) {
              // Jumped over
            } else if (obs.type === 'barrier' && state.player.isSliding) {
              handleCollision();
            } else if (obs.type === 'low_barrier' && state.player.isSliding) {
              // Slid under
            } else {
              handleCollision();
            }
          }
        }

        return obs.y < height + 80;
      });

      // Update and draw collectibles
      state.collectibles = state.collectibles.filter(col => {
        col.y += state.speed;
        
        const colX = getLaneX(col.lane);
        
        if (col.type === 'coin') {
          ctx.beginPath();
          ctx.arc(colX, col.y + col.height/2, 10, 0, Math.PI * 2);
          ctx.fillStyle = '#fbbf24';
          ctx.fill();
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(colX - 3, col.y + col.height/2 - 3, 3, 0, Math.PI * 2);
          ctx.fillStyle = '#fef3c7';
          ctx.fill();
        } else if (col.type === 'magnet') {
          ctx.fillStyle = '#ec4899';
          ctx.fillRect(colX - 8, col.y, 16, 20);
        } else if (col.type === 'multiplier') {
          ctx.fillStyle = '#22c55e';
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('x2', colX, col.y + 15);
        } else if (col.type === 'shield') {
          ctx.beginPath();
          ctx.arc(colX, col.y + col.height/2, 12, 0, Math.PI * 2);
          ctx.fillStyle = '#3b82f6';
          ctx.fill();
          ctx.fillStyle = '#93c5fd';
          ctx.font = 'bold 12px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('S', colX, col.y + 16);
        }

        // Collection detection
        const colLeft = colX - col.width / 2;
        const colRight = colX + col.width / 2;
        const colTop = col.y;
        const colBottom = col.y + col.height;

        if (playerRight > colLeft && playerLeft < colRight &&
            playerBottom > colTop && playerTop < colBottom) {
          
          if (col.type === 'coin') {
            setCoins(prev => prev + 1);
            setScore(prev => prev + 50 * multiplier);
          } else if (col.type === 'multiplier') {
            setMultiplier(2);
            setPowerUp('x2');
            setTimeout(() => {
              setMultiplier(1);
              setPowerUp(null);
            }, 10000);
          } else if (col.type === 'shield') {
            state.invincible = true;
            state.invincibleTimer = 300;
            setPowerUp('shield');
            setTimeout(() => setPowerUp(null), 5000);
          }
          
          return false;
        }

        return col.y < height + 40;
      });

      // Draw player
      const px = getLaneX(state.player.lane);
      
      // Player shadow
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(px, state.player.baseY + state.player.height - 4, 18, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Player body
      if (state.invincible && state.frameCount % 10 < 5) {
        ctx.fillStyle = '#3b82f6';
      } else {
        ctx.fillStyle = '#8b5cf6';
      }
      
      const playerDrawY = state.player.isSliding ? state.player.y + 22 : state.player.y;
      const playerDrawHeight = state.player.isSliding ? 23 : 45;
      
      ctx.fillRect(px - 15, playerDrawY, 30, playerDrawHeight);
      
      // Player head
      ctx.beginPath();
      ctx.arc(px, playerDrawY - 12, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#fcd34d';
      ctx.fill();
      
      // Player face
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(px - 6, playerDrawY - 16, 3, 5);
      ctx.fillRect(px + 3, playerDrawY - 16, 3, 5);
      ctx.fillRect(px - 5, playerDrawY - 6, 10, 2);

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, isPaused, multiplier, spawnObstacle, spawnCollectible, canvasSize, getLaneX]);

  // Handle collision
  const handleCollision = () => {
    const state = gameState.current;
    if (state.invincible) return;
    
    setLives(prev => {
      const newLives = prev - 1;
      if (newLives <= 0 && session?.difficulty !== 'basic') {
        setGameOver(true);
      } else {
        // Brief invincibility after hit
        state.invincible = true;
        state.invincibleTimer = 60;
      }
      return Math.max(0, newLives);
    });
  };

  // End game
  const endGame = async (won) => {
    if (!session || endingGame) return;
    
    // Save high score
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('rushRunnerHighScore', score.toString());
    }
    
    setEndingGame(true);

    try {
      const res = await api.post('/games/rush-runner/end', {
        session_id: session.sessionId,
        score,
        won,
        time_played_seconds: timeElapsed
      });

      setResult(res.data);
      localStorage.removeItem('currentGameSession');
      
      toast({
        title: won ? "Amazing Run!" : "Game Over",
        description: won && res.data.reward_earned > 0 
          ? `You earned ${res.data.reward_earned} AGP!` 
          : `Distance: ${distance}m | Coins: ${coins}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to end game",
        variant: "destructive"
      });
    } finally {
      setEndingGame(false);
    }
  };

  // Handle game over
  useEffect(() => {
    if (gameOver && !result) {
      endGame(score > 500);
    }
  }, [gameOver, result, score]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Game Over!</h2>
            <p className="text-muted-foreground mb-4">Great run!</p>

            <div className="space-y-3 my-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Score</span>
                <span className="font-bold">{score}</span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Distance</span>
                <span className="font-bold">{distance}m</span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Coins</span>
                <span className="font-bold text-yellow-400">{coins}</span>
              </div>
              {score > highScore - 100 && (
                <div className="flex justify-between p-3 bg-yellow-500/20 rounded-lg">
                  <span>High Score</span>
                  <span className="font-bold text-yellow-400">{Math.max(score, highScore)}</span>
                </div>
              )}
              {result.reward_earned > 0 && (
                <div className="flex justify-between p-3 bg-green-500/20 rounded-lg">
                  <span>Reward</span>
                  <span className="font-bold text-green-400">+{result.reward_earned} AGP</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/games')}>
                Game Zone
              </Button>
              <Button className="flex-1" onClick={() => navigate('/games')}>
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Start screen
  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="text-6xl mb-4">🏃</div>
            <h2 className="text-2xl font-bold mb-2">Rush Runner</h2>
            <p className="text-muted-foreground mb-6">
              Dodge obstacles, collect coins, run as far as you can!
            </p>
            
            <div className="bg-muted p-4 rounded-lg mb-6 text-left">
              <h3 className="font-bold mb-2">Controls:</h3>
              <p className="text-sm text-muted-foreground">
                ⬅️ ➡️ - Switch lanes<br/>
                ⬆️ / Space - Jump<br/>
                ⬇️ - Slide
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-2xl mb-1">🟡</div>
                <div className="text-muted-foreground">Coins +50</div>
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-2xl mb-1">🛡️</div>
                <div className="text-muted-foreground">Shield</div>
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-2xl mb-1">x2</div>
                <div className="text-muted-foreground">Multiplier</div>
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <Heart className="w-6 h-6 text-red-400 mx-auto mb-1" />
                <div className="text-muted-foreground">{lives} Lives</div>
              </div>
            </div>

            {highScore > 0 && (
              <div className="mb-4 text-sm text-muted-foreground">
                High Score: <span className="text-yellow-400 font-bold">{highScore}</span>
              </div>
            )}

            <Button className="w-full" size="lg" onClick={initGame}>
              <Play className="w-5 h-5 mr-2" />
              Start Running
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="rush-runner-game">
      {/* HUD */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-lg mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => {
              if (confirm('Exit game?')) {
                endGame(score > 500);
              }
            }}>
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exit</span>
            </Button>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                <span className="font-bold">{score}</span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                <span className="font-bold">{coins}</span>
              </div>

              {session?.difficulty !== 'basic' && (
                <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                  <span className="font-bold">{lives}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {powerUp && (
                <div className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-500/20 text-green-400 rounded text-[10px] sm:text-xs font-bold">
                  {powerUp === 'x2' ? 'x2' : 'SHIELD'}
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={() => setIsPaused(!isPaused)}>
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Game Canvas */}
      <div className="flex flex-col items-center p-2 sm:p-4">
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="rounded-lg border-2 border-primary/30"
            style={{ touchAction: 'none' }}
          />
          
          {/* Distance indicator */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/50 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm">
            {distance}m
          </div>

          {isPaused && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-lg">
              <Card className="p-4 sm:p-6 text-center">
                <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Paused</h2>
                <Button onClick={() => setIsPaused(false)}>
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              </Card>
            </div>
          )}
        </div>

        {/* Mobile Controls - Always visible */}
        <div className="w-full max-w-xs mt-4 px-2">
          <div className="grid grid-cols-3 gap-2">
            <Button 
              size="lg" 
              className="h-14 sm:h-16 text-xl"
              onTouchStart={(e) => { e.preventDefault(); handleSwipe('left'); }}
              onClick={() => handleSwipe('left')}
            >
              ⬅️
            </Button>
            <div className="grid grid-rows-2 gap-1">
              <Button 
                size="lg" 
                className="h-6 sm:h-7 text-sm"
                onTouchStart={(e) => { e.preventDefault(); handleSwipe('up'); }}
                onClick={() => handleSwipe('up')}
              >
                ⬆️
              </Button>
              <Button 
                size="lg" 
                className="h-6 sm:h-7 text-sm"
                onTouchStart={(e) => { e.preventDefault(); handleSwipe('down'); }}
                onClick={() => handleSwipe('down')}
              >
                ⬇️
              </Button>
            </div>
            <Button 
              size="lg" 
              className="h-14 sm:h-16 text-xl"
              onTouchStart={(e) => { e.preventDefault(); handleSwipe('right'); }}
              onClick={() => handleSwipe('right')}
            >
              ➡️
            </Button>
          </div>
          
          <p className="text-center text-xs text-muted-foreground mt-2">
            Tap buttons or use Arrow keys
          </p>
        </div>
      </div>
    </div>
  );
};

export default RushRunnerGame;
